import React, { FC, createRef, useRef } from 'react';
import { Box, Button, CircularProgress, Fade, Link, Typography } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

export const CODE_LENGTH = 8;

export interface JoinTeamStepProps {
  accentColor: string;
  teamCode: string[];
  hasError: boolean;
  isLoading: boolean;
  onChange: (idx: number, val: string) => void;
  onJoin: () => void;
  onSkip: () => void;
  onBack: () => void;
}

const JoinTeamStep: FC<JoinTeamStepProps> = ({
  accentColor,
  teamCode,
  hasError,
  isLoading,
  onChange,
  onJoin,
  onSkip,
  onBack,
}) => {
  const inputRefs = useRef(Array.from({ length: CODE_LENGTH }, () => createRef<HTMLInputElement>()));

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (teamCode[idx] !== '') {
        onChange(idx, '');
      } else if (idx > 0) {
        onChange(idx - 1, '');
        inputRefs.current[idx - 1].current?.focus();
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      inputRefs.current[idx - 1].current?.focus();
    } else if (e.key === 'ArrowRight' && idx < CODE_LENGTH - 1) {
      inputRefs.current[idx + 1].current?.focus();
    } else if (e.key === 'Enter') {
      const filled = teamCode.every((c) => c !== '');
      if (filled) onJoin();
    }
  };

  const handleInput = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (!raw) return;
    // Handle paste of multiple characters
    const chars = raw.slice(0, CODE_LENGTH - idx).split('');
    chars.forEach((ch, offset) => {
      onChange(idx + offset, ch);
    });
    const nextIdx = Math.min(idx + chars.length, CODE_LENGTH - 1);
    inputRefs.current[nextIdx].current?.focus();
  };

  if (isLoading) {
    return (
      <Fade in>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
            gap: 2,
          }}
        >
          <CircularProgress size={48} sx={{ color: accentColor }} />
          <Typography variant="body2" color="text.secondary">
            Joining your team…
          </Typography>
        </Box>
      </Fade>
    );
  }

  const isComplete = teamCode.every((c) => c !== '');

  return (
    <Box>
      {/* Back link */}
      <Link
        component="button"
        onClick={onBack}
        underline="none"
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          fontSize: 14,
          mb: 3,
          '&:hover': { color: 'text.primary' },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 18, mr: 0.25 }} />
        Back
      </Link>

      {/* Icon badge */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2.5,
          backgroundColor: `${accentColor}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
        }}
      >
        <GroupsIcon sx={{ color: accentColor, fontSize: 26 }} />
      </Box>

      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Join a Team
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3} sx={{ lineHeight: 1.6 }}>
        Enter the 8-character code from your coach to connect with your team.
      </Typography>

      {/* Code input */}
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ color: hasError ? 'error.main' : accentColor, display: 'block', mb: 1 }}
      >
        Team Code
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        {Array.from({ length: CODE_LENGTH }).map((_, idx) => (
          <Box
            key={idx}
            component="input"
            autoCapitalize="characters"
            maxLength={1}
            value={teamCode[idx]}
            ref={inputRefs.current[idx] as React.RefObject<HTMLInputElement>}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInput(idx, e)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(idx, e)}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
            sx={{
              width: 48,
              height: 52,
              borderRadius: '8px',
              border: hasError ? '1.5px solid' : '1.5px solid',
              borderColor: hasError ? 'error.main' : 'divider',
              backgroundColor: hasError ? 'error.50' : 'background.paper',
              textAlign: 'center',
              textTransform: 'uppercase',
              fontSize: 20,
              fontWeight: 600,
              color: hasError ? 'error.main' : 'text.primary',
              outline: 'none',
              fontFamily: 'inherit',
              cursor: 'text',
              transition: 'border-color 0.15s',
              '&:focus': {
                borderColor: hasError ? 'error.main' : accentColor,
                boxShadow: hasError ? '0 0 0 3px rgba(211,47,47,0.12)' : `0 0 0 3px ${accentColor}22`,
              },
            }}
          />
        ))}
      </Box>

      {/* Helper / error text */}
      <Typography variant="caption" sx={{ color: hasError ? 'error.main' : 'text.secondary', display: 'block', mb: 3 }}>
        {hasError
          ? 'Code not found. Double-check with your coach and try again.'
          : 'Ask your coach for this 8-character code.'}
      </Typography>

      {/* Primary button */}
      <Button
        variant="contained"
        fullWidth
        disabled={!isComplete && !hasError}
        onClick={hasError ? onJoin : onJoin}
        sx={{
          borderRadius: 999,
          py: 1.25,
          fontWeight: 600,
          fontSize: 15,
          mb: 1.5,
          backgroundColor: accentColor,
          boxShadow: 'none',
          '&:hover': { backgroundColor: accentColor, filter: 'brightness(0.9)', boxShadow: 'none' },
          '&.Mui-disabled': { backgroundColor: `${accentColor}50`, color: '#fff' },
        }}
      >
        {hasError ? 'Try Again' : 'Join Team'}
      </Button>

      {/* Skip for Now */}
      <Box sx={{ textAlign: 'center' }}>
        <Link
          component="button"
          onClick={onSkip}
          underline="none"
          sx={{
            color: 'text.secondary',
            fontSize: 14,
            '&:hover': { color: 'text.primary' },
          }}
        >
          Skip for Now
        </Link>
      </Box>
    </Box>
  );
};

export default JoinTeamStep;
